import React, { useState, useEffect, useRef } from 'react';
import { ChatConfig, ChatMessage, ConversationHistory } from '../types';
import { ChatGPTService } from '../services/chatgpt';
import { EmailService } from '../services/emailService';

interface ChatInterfaceProps {
  config: ChatConfig;
  conversationHistory: ConversationHistory;
  setConversationHistory: React.Dispatch<React.SetStateAction<ConversationHistory>>;
  onClearHistory: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  config, 
  conversationHistory, 
  setConversationHistory, 
  onClearHistory
}) => {
  const [userMessage, setUserMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<{ success: boolean; message: string } | null>(null);
  const debugEnvEnabled = String(process.env.REACT_APP_DEBUG).toLowerCase() === 'true';
  const [debugMode, setDebugMode] = useState(debugEnvEnabled);
  const [debugSystemPrompt, setDebugSystemPrompt] = useState<string | null>(null);
  const [debugBackendResponse, setDebugBackendResponse] = useState<string | null>(null);

  const conversationHistoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (conversationHistoryRef.current) {
      // Scroll to the bottom to show the latest response
      conversationHistoryRef.current.scrollTop = conversationHistoryRef.current.scrollHeight;
    }
  }, [conversationHistory.messages]);

  // Debug effect pour surveiller les changements de detectedLabels
  useEffect(() => {
    console.log('🔄 ConversationHistory mis à jour:', {
      messagesCount: conversationHistory.messages.length,
      detectedLabelsCount: conversationHistory.detectedLabels?.length || 0,
      detectedLabels: conversationHistory.detectedLabels
    });
  }, [conversationHistory.detectedLabels]);

  const sendConversationByEmail = async (conversationHistory: ConversationHistory) => {
    try {
      const emailContent = `
        <h2>Conversation avec JudicIA - Assistant Legal IA</h2>
        <p><strong>Date:</strong> ${new Date().toLocaleString('fr-FR')}</p>
        <hr>
        ${conversationHistory.messages.map((message, index) => `
          <div style="margin-bottom: 20px; padding: 15px; border-left: 4px solid ${message.role === 'user' ? '#007bff' : '#28a745'}; background-color: #f8f9fa;">
            <h4 style="margin: 0 0 10px 0; color: ${message.role === 'user' ? '#007bff' : '#28a745'};">
              ${message.role === 'user' ? 'Vous' : 'JudicIA'}
            </h4>
            <p style="margin: 0; white-space: pre-wrap;">${message.content}</p>
            <small style="color: #666; font-size: 12px;">
              ${message.timestamp ? new Date(message.timestamp).toLocaleString('fr-FR') : ''}
            </small>
          </div>
        `).join('')}
        <hr>
        <p><strong>Total des messages:</strong> ${conversationHistory.messages.length}</p>
        <p><strong>Tokens estimés:</strong> ${conversationHistory.totalTokens}</p>
      `;

      const emailData = {
        to: 'anne-valentine.rensonnet@avocat.be',
        subject: 'Conversation JudicIA - Prise de contact automatique',
        content: emailContent,
        isHtml: true
      };

      const result = await EmailService.sendEmail(emailData);
      setEmailStatus({
        success: result.success,
        message: result.success ? 'Email envoyé avec succès' : result.message
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      setEmailStatus({
        success: false,
        message: 'Erreur lors de l\'envoi de l\'email'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userMessage.trim() || isLoading) return;

    const userMessageWithTimestamp: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };

    setConversationHistory(prev => ({
      messages: [...prev.messages, userMessageWithTimestamp],
      totalTokens: prev.totalTokens,
      detectedLabels: prev.detectedLabels
    }));

    setUserMessage('');
    setIsLoading(true);
    setError(null);

    // If debug mode is active, get the enriched system prompt
    if (debugMode) {
      try {
        const enrichedSystemPrompt = await ChatGPTService.getEnrichedSystemPrompt(config, conversationHistory, userMessage);
        setDebugSystemPrompt(enrichedSystemPrompt);
        
        // Récupérer aussi la réponse du backend pour le debug
        const backendResponse = await ChatGPTService.getBackendResponse(config, conversationHistory, userMessage);
        setDebugBackendResponse(backendResponse);
      } catch (error) {
        console.error('Erreur lors de la récupération du prompt système:', error);
        setDebugSystemPrompt(config.systemPrompt);
        setDebugBackendResponse('Erreur lors de la récupération de la réponse backend');
      }
    }

    try {
      const result = await ChatGPTService.sendMessage(userMessage, config, conversationHistory);
      const response = result.response;
      const updatedConversationHistory = result.updatedConversationHistory;
      
      // Check if response contains [PRISE-CONTACT]
      const hasPriseContact = response.content.includes('[PRISE-CONTACT]');
      
      // Create cleaned response for display (without [PRISE-CONTACT])
      const cleanedResponse: ChatMessage = {
        role: 'assistant',
        content: hasPriseContact ? response.content.replace(/\[PRISE-CONTACT\]/g, '').trim() : response.content,
        timestamp: new Date()
      };

      // Update conversation history with cleaned response and updated detectedLabels
      setConversationHistory(prev => ({
        messages: [...prev.messages, cleanedResponse],
        totalTokens: response.usage?.total_tokens || prev.totalTokens,
        detectedLabels: updatedConversationHistory.detectedLabels // Use the updated detectedLabels
      }));

      // If [PRISE-CONTACT] was detected, send email with original conversation
      if (hasPriseContact) {
        const conversationWithPriseContact = {
          messages: [...conversationHistory.messages, userMessageWithTimestamp, {
            role: 'assistant' as const,
            content: response.content,
            timestamp: new Date()
          }],
          totalTokens: response.usage?.total_tokens || conversationHistory.totalTokens,
          detectedLabels: updatedConversationHistory.detectedLabels
        };
        await sendConversationByEmail(conversationWithPriseContact);
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setError('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
      // Debug system prompt stays displayed when debug mode is active
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="professional-bg" style={{ minHeight: '100%' }}>
      {debugEnvEnabled && (
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <button
              onClick={() => debugEnvEnabled && setDebugMode(!debugMode)}
              style={{
                background: debugMode ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              {debugMode ? '🔴 Mode Debug ON' : '🔵 Mode Debug OFF'}
            </button>
          </div>
        </div>
      )}

      {/* Debug Backend Response Display */}
      {debugEnvEnabled && debugMode && (
        <div style={{
          background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
          color: 'white',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '2px solid #e74c3c',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '1.2rem', marginRight: '8px' }}>🔵</span>
            <strong style={{ fontSize: '1rem' }}>Réponse de l'Agent Backend (Mode Debug)</strong>
          </div>
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '0.9rem',
            lineHeight: '1.4',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {debugBackendResponse || 'En attente de la réponse du backend...'}
          </div>
        </div>
      )}

      {/* Debug System Prompt Display */}
      {debugEnvEnabled && debugMode && debugSystemPrompt && (
        <div style={{
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          color: 'white',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '2px solid #e74c3c',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '1.2rem', marginRight: '8px' }}>🔍</span>
            <strong style={{ fontSize: '1rem' }}>Prompt Système de l'Agent Frontend (Mode Debug)</strong>
          </div>
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '0.9rem',
            lineHeight: '1.4',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {debugSystemPrompt}
          </div>
        </div>
      )}

      {/* Debug Labels Memory Display */}
      {debugEnvEnabled && debugMode && (
        <div style={{
          background: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)',
          color: 'white',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '2px solid #e74c3c',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '1.2rem', marginRight: '8px' }}>🏷️</span>
            <strong style={{ fontSize: '1rem' }}>Mémoire des Labels Détectés (Mode Debug)</strong>
          </div>
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '0.9rem',
            lineHeight: '1.4',
            fontFamily: 'monospace',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {/* Debug info */}
            <div style={{ marginBottom: '10px', padding: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px' }}>
              <strong>Debug Info:</strong><br/>
              detectedLabels existe: {conversationHistory.detectedLabels ? 'OUI' : 'NON'}<br/>
              Nombre de labels: {conversationHistory.detectedLabels ? conversationHistory.detectedLabels.length : 0}<br/>
              Contenu: {JSON.stringify(conversationHistory.detectedLabels)}
            </div>
            
            {/* Labels display */}
            {conversationHistory.detectedLabels && conversationHistory.detectedLabels.length > 0 ? (
              conversationHistory.detectedLabels.map((detectedLabel, index) => {
                console.log('🏷️ Affichage du label:', detectedLabel);
                return (
                  <div key={index} style={{ 
                    marginBottom: '8px', 
                    padding: '8px', 
                    background: 'rgba(255,255,255,0.1)', 
                    borderRadius: '4px',
                    borderLeft: `3px solid ${detectedLabel.source === 'backend' ? '#3498db' : '#e74c3c'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <strong style={{ color: detectedLabel.source === 'backend' ? '#3498db' : '#e74c3c' }}>
                        [{detectedLabel.label}]
                      </strong>
                      <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                        {detectedLabel.source === 'backend' ? '🔵 Backend' : '🔴 Frontend'} - {formatTimestamp(detectedLabel.timestamp)}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ 
                padding: '8px', 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: '4px',
                fontStyle: 'italic',
                opacity: 0.8
              }}>
                Aucun label détecté pour le moment
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Historique des messages */}
      <div className="conversation-history" ref={conversationHistoryRef}>
        {conversationHistory.messages.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            color: '#7f8c8d', 
            fontStyle: 'italic',
            padding: '40px 20px',
            fontSize: '1.1rem'
          }}>
            <div style={{ marginBottom: '15px', fontSize: '3rem' }}>⚖️</div>
            Bienvenue dans votre espace de consultation juridique
            <br />
            <span style={{ fontSize: '0.9rem', marginTop: '10px', display: 'block' }}>
              Posez votre question pour commencer
            </span>
          </div>
        )}
        
        {conversationHistory.messages.map((message, index) => (
          <div
            key={index}
            className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
            style={{
              animation: 'fadeInUp 0.5s ease-out',
              animationDelay: `${index * 0.1}s`
            }}
          >
            <div className="message-header">
              <strong style={{ 
                fontSize: '0.95rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {message.role === 'user' ? 'Vous' : 'JudicIA'}
              </strong>
              <span className="timestamp">{formatTimestamp(message.timestamp!)}</span>
            </div>
            <div className="message-content">
              {message.content}
            </div>
          </div>
        ))}
        
        {/* Indicateur de chargement pendant l'attente de la réponse */}
        {isLoading && (
          <div
            className="message assistant-message"
            style={{
              animation: 'fadeInUp 0.5s ease-out',
              opacity: 0.8
            }}
          >
            <div className="message-header">
              <strong style={{ 
                fontSize: '0.95rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                JudicIA
              </strong>
              <span className="timestamp">En cours...</span>
            </div>
            <div className="message-content" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              fontStyle: 'italic',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              <span className="loading"></span>
              Analyse de votre question en cours...
            </div>
          </div>
        )}
      </div>

      {/* Formulaire d'envoi */}
      <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-end',
          gap: '10px',
          background: 'white',
          border: '2px solid #e9ecef',
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ flex: 1 }}>
            <textarea
              id="userMessage"
              rows={3}
              placeholder="Tapez votre message ici..."
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              disabled={isLoading}
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontSize: '1rem',
                lineHeight: '1.5',
                fontFamily: 'Georgia, serif',
                background: 'transparent'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!isLoading && userMessage.trim()) {
                    const formEvent = { preventDefault: () => {} } as React.FormEvent;
                    handleSubmit(formEvent);
                  }
                }
              }}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !userMessage.trim()}
            style={{
              background: isLoading ? '#95a5a6' : 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 16px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.3s ease',
              minWidth: '80px',
              justifyContent: 'center'
            }}
          >
            {isLoading ? (
              <>
                <span className="loading"></span>
                <span>...</span>
              </>
            ) : (
              <>
                <span style={{ fontSize: '1.1rem' }}>➤</span>
                <span>Envoyer</span>
              </>
            )}
          </button>
        </div>
      </form>

      {error && (
        <div className="status-message status-error" style={{ marginTop: '20px' }}>
          <span style={{ fontSize: '1.2rem' }}>⚠️</span>
          <strong>Erreur:</strong> {error}
        </div>
      )}

      {emailStatus && (
        <div 
          className={`status-message ${emailStatus.success ? 'status-success' : 'status-error'}`}
          style={{ marginTop: '20px' }}
        >
          <span style={{ fontSize: '1.2rem' }}>
            {emailStatus.success ? '📧' : '⚠️'}
          </span>
          <strong>Notification automatique:</strong> {emailStatus.message}
        </div>
      )}

      <div style={{ fontSize: '0.85rem', textAlign: 'center', color: 'gray', marginBottom: '30px' }}>
      <div>
      {/* Trigger */}
      <p> JudicIA peut faire des erreurs. &nbsp;
      <a
  href="/conditions-utilisation.pdf"
  download
  style={{
    cursor: 'pointer',
    color: 'inherit',
    textDecoration: 'none'
  }}
>
  Vérifier les conditions d'utilisation ici.
</a>
      </p>

      {/* Modal */}
      {isOpen && (
        <div style={styles.overlay} onClick={() => setIsOpen(false)}>
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
          >
            <span style={styles.close} onClick={() => setIsOpen(false)}>
              &times;
            </span>
            <h2>Conditions d'utilisation</h2>
 
     <h2>1. Description du service</h2>
    <p>Notre chatbot fournit des informations juridiques générales en utilisant l'API ChatGPT de OpenAI. Il ne remplace pas les conseils d'un avocat ou d'un professionnel du droit.</p>

    <h2>2. Limitation de responsabilité</h2>
    <p>Les informations fournies par le chatbot sont à titre informatif uniquement. Nous ne garantissons pas l'exactitude, l'exhaustivité ou l'actualité des informations fournies. En aucun cas, nous ne pourrons être tenus responsables de tout dommage résultant de l'utilisation de ce service.</p>

    <h2>3. Confidentialité et données</h2>
    <ul>
        <li>Les conversations peuvent être stockées pour améliorer le service.</li>
        <li>Nous ne collectons pas intentionnellement de données sensibles sans consentement explicite.</li>
        <li>Il est conseillé de ne pas partager d'informations personnelles identifiables dans le chatbot.</li>
    </ul>

    <h2>4. Utilisation acceptable</h2>
    <p>Vous vous engagez à ne pas utiliser le chatbot pour :</p>
    <ul>
        <li>Partager des contenus illégaux, diffamatoires ou offensants.</li>
        <li>Tenter d'extraire ou manipuler l'API de manière non autorisée.</li>
        <li>Utiliser le service à des fins commerciales sans accord préalable.</li>
    </ul>

    <h2>5. Modification des conditions</h2>
    <p>Nous nous réservons le droit de modifier ces conditions à tout moment. Les modifications seront publiées sur cette page et prendront effet immédiatement après publication.</p>

    <h2>6. Acceptation des conditions</h2>
    <p>En utilisant ce chatbot, vous acceptez ces conditions d'utilisation dans leur intégralité.</p>

    <p>Pour toute question concernant ces conditions, veuillez nous contacter à <a href="mailto:support@votredomaine.com">support@votredomaine.com</a>.</p>

            {/* Add more terms content here */}
          </div>
        </div>
      )}
    </div>
      </div>
    </div>
  
  );
}; 

// Inline styles
const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    width: '60%',
    maxWidth: '600px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
    position: 'relative',
    fontSize: '14px',
    color: '#333'
  },
  close: {
    position: 'absolute',
    top: '10px',
    right: '16px',
    fontSize: '12px',
    fontWeight: 'normal',
    color: '#666',
    cursor: 'pointer',
    textDecoration: 'none', // removes underline
    background: 'none', // removes button background
    border: 'none', // removes button border
    padding: 0 // ensures no default padding
  }
};
